import { Grid, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'

const formatNumberDisplay = (value: string): string => {
  if (!value) return ''
  
  const num = parseFloat(value.replace(/,/g, ''))
  if (isNaN(num)) return value
  
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const parseFormattedNumber = (value: string): string => {
  return value.replace(/,/g, '')
}

interface MinMaxInputProps {
  size?: 'small' | 'medium'
  minLabel?: string
  maxLabel?: string
  minPlaceholder?: string
  maxPlaceholder?: string
  minValue: string
  maxValue: string
  onValueChange: (values: { min: string; max: string }) => void
  minError?: boolean
  maxError?: boolean
  minHelperText?: string
  maxHelperText?: string
  onMinBlur?: () => void
  onMaxBlur?: () => void
}

export const MinMaxInput: React.FC<MinMaxInputProps> = ({
  size = 'small',
  minLabel = 'Min Value',
  maxLabel = 'Max Value',
  minPlaceholder = 'Enter minimum value',
  maxPlaceholder = 'Enter maximum value',
  minValue,
  maxValue,
  onValueChange,
  minError,
  maxError,
  minHelperText,
  maxHelperText,
  onMinBlur,
  onMaxBlur,
}) => {
  const [displayMinValue, setDisplayMinValue] = useState('')
  const [displayMaxValue, setDisplayMaxValue] = useState('')
  const [isMinFocused, setIsMinFocused] = useState(false)
  const [isMaxFocused, setIsMaxFocused] = useState(false)

  useEffect(() => {
    if (!isMinFocused) {
      setDisplayMinValue(minValue ? formatNumberDisplay(minValue) : '')
    }
  }, [minValue, isMinFocused])

  useEffect(() => {
    if (!isMaxFocused) {
      setDisplayMaxValue(maxValue ? formatNumberDisplay(maxValue) : '')
    }
  }, [maxValue, isMaxFocused])

  const handleChange = (type: 'min' | 'max', value: string) => {
    if (type === 'min') {
      setDisplayMinValue(value)
    } else {
      setDisplayMaxValue(value)
    }
    
    const rawValue = parseFormattedNumber(value)
    const newValues = {
      min: type === 'min' ? rawValue : minValue,
      max: type === 'max' ? rawValue : maxValue,
    }
    onValueChange(newValues)
  }

  const handleFocus = (type: 'min' | 'max') => {
    if (type === 'min') {
      setIsMinFocused(true)
      setDisplayMinValue(minValue)
    } else {
      setIsMaxFocused(true)
      setDisplayMaxValue(maxValue)
    }
  }

  const handleBlur = (type: 'min' | 'max') => {
    if (type === 'min') {
      setIsMinFocused(false)
      if (onMinBlur) onMinBlur()
    } else {
      setIsMaxFocused(false)
      if (onMaxBlur) onMaxBlur()
    }
  }

  return (
    <Grid container spacing={2} direction={'column'}>
      <Grid item>
        <TextField
          variant='outlined'
          size={size}
          label={minLabel}
          placeholder={minPlaceholder}
          value={displayMinValue}
          onChange={(e) => handleChange('min', e.target.value)}
          onFocus={() => handleFocus('min')}
          onBlur={() => handleBlur('min')}
          fullWidth
          type='text'
          error={!!minError}
          helperText={minHelperText}
        />
      </Grid>
      <Grid item>
        <TextField
          size={size}
          variant='outlined'
          label={maxLabel}
          placeholder={maxPlaceholder}
          value={displayMaxValue}
          onChange={(e) => handleChange('max', e.target.value)}
          onFocus={() => handleFocus('max')}
          onBlur={() => handleBlur('max')}
          fullWidth
          type='text'
          error={!!maxError}
          helperText={maxHelperText}
        />
      </Grid>
    </Grid>
  )
}
